// HouseProud sub-brand hero. Big-blue-gradient block that pitches the
// homeowner-side property logbook as bundled with the tradesperson's
// Manos subscription. Lives only on per-trade marketing pages where
// property work is actually relevant (plumbers, electricians, gas
// engineers, builders, painters, locksmiths, landscapers, cleaners --
// see PROPERTY_TRADE_SLUGS in @/lib/for-trades).
//
// Intentionally NOT on the Manos homepage. The homepage sells Manos to
// every trade, but HouseProud only matters to property-trade audiences;
// putting it on the homepage misled visitors who run mobile or service
// businesses (tutors, hair stylists, mobile mechanics, etc.) into
// thinking the property logbook was for them. Move was made on
// 2026-04-26 after that exact feedback.
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  CheckCircle,
  House,
} from "@phosphor-icons/react";
import { useVisitorGeo, getFormatsForCountry } from "@barrykilby/geo";
import { FadeInOnScroll } from "@/components/ui/FadeInOnScroll";

// Country-keyed sample log-book entries. Each row mirrors the same
// shape (label / sub / badge) but swaps regulatory terminology and
// currency to match the visitor's market. UK shows Gas Safe + EICR;
// US shows NATE + electrical safety inspection; CA mirrors US for
// trade vocabulary today (separate variants land if/when CA-specific
// terms diverge meaningfully). Visitors outside the explicit map fall
// back to a country-neutral set with the visitor's currency on the
// money-row.
type Entry = {
  label: string;
  sub: string;
  badge: string;
  badgeColor: string;
  dot: string;
};

function buildEntries(country: string): Entry[] {
  const formats = getFormatsForCountry(country);
  const fmtMoney = (n: number) =>
    new Intl.NumberFormat(formats.dateLocale, {
      style: "currency",
      currency: formats.currencyCode,
      maximumFractionDigits: 0,
    }).format(n);

  const c = country.toUpperCase();
  if (c === "GB" || c === "IE") {
    return [
      {
        label: "Boiler service -- annual",
        sub: "Dave's Plumbing · 12 Mar 2026",
        badge: "Gas Safe ✓",
        badgeColor: "text-green-700 bg-green-50",
        dot: "bg-green-500",
      },
      {
        label: "Bathroom refit -- complete",
        sub: `ABC Builders · 28 Feb 2026`,
        badge: `${fmtMoney(2400)} paid`,
        badgeColor: "text-blue-700 bg-blue-50",
        dot: "bg-blue-500",
      },
      {
        label: "EICR -- due soon",
        sub: "Electrical check expires Jul 2026",
        badge: "Renew soon",
        badgeColor: "text-amber-700 bg-amber-50",
        dot: "bg-amber-500",
      },
    ];
  }

  // North America (US / CA) -- NATE-certified HVAC + permit inspection.
  if (c === "US" || c === "CA") {
    return [
      {
        label: "HVAC service -- annual",
        sub: "Acme HVAC · Mar 12, 2026",
        badge: "NATE certified ✓",
        badgeColor: "text-green-700 bg-green-50",
        dot: "bg-green-500",
      },
      {
        label: "Bathroom remodel -- complete",
        sub: "Bayside Builders · Feb 28, 2026",
        badge: `${fmtMoney(2400)} paid`,
        badgeColor: "text-blue-700 bg-blue-50",
        dot: "bg-blue-500",
      },
      {
        label: "Electrical inspection -- due soon",
        sub: "Permit check expires Jul 2026",
        badge: "Renew soon",
        badgeColor: "text-amber-700 bg-amber-50",
        dot: "bg-amber-500",
      },
    ];
  }

  // Country-neutral fallback (AU, NZ, EU, etc): drop UK/US-specific
  // certification names, keep the receipt-style middle row.
  return [
    {
      label: "Service visit -- annual",
      sub: "Local trader · Mar 12, 2026",
      badge: "Service log ✓",
      badgeColor: "text-green-700 bg-green-50",
      dot: "bg-green-500",
    },
    {
      label: "Renovation -- complete",
      sub: "Local trader · Feb 28, 2026",
      badge: `${fmtMoney(2400)} paid`,
      badgeColor: "text-blue-700 bg-blue-50",
      dot: "bg-blue-500",
    },
    {
      label: "Inspection -- due soon",
      sub: "Renewal expected mid-2026",
      badge: "Renew soon",
      badgeColor: "text-amber-700 bg-amber-50",
      dot: "bg-amber-500",
    },
  ];
}

export function HouseProudHero() {
  const visitorGeo = useVisitorGeo();
  const country = visitorGeo?.country ?? "GB";
  const entries = buildEntries(country);
  return (
    <FadeInOnScroll>
      <section className="py-24 bg-gradient-to-br from-blue-900 to-blue-800 text-white overflow-hidden relative">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-400 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-blue-300 rounded-full blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-6xl px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left -- copy */}
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-3 flex-wrap">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20">
                    <House className="h-4 w-4 text-blue-200" weight="duotone" />
                    <span className="text-xs font-semibold text-blue-100 uppercase tracking-wider">
                      Included free for your customers
                    </span>
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500 text-white shadow-sm">
                    <span className="h-2 w-2 rounded-full bg-amber-200 animate-pulse" />
                    <span className="text-xs font-bold uppercase tracking-wider">
                      Coming soon
                    </span>
                  </div>
                </div>
                <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight text-white">
                  Every job you do builds<br />
                  your customer&apos;s <span className="text-blue-200">property history</span>
                </h2>
                <p className="text-blue-100 leading-relaxed max-w-md">
                  HouseProud is a digital logbook for homeowners. When you
                  complete work on their property, it&apos;s automatically
                  logged -- certificates, photos, invoices -- and they can
                  share it with estate agents, insurers, and future buyers.
                </p>
              </div>
              <ul className="space-y-3">
                {[
                  "Property logbook built automatically from your work",
                  "Gas Safe, EICR, and other certificates stored permanently",
                  "Customers can share their full service history in one tap",
                  "A reason customers choose you -- and come back to you",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-blue-100">
                    <CheckCircle className="h-5 w-5 text-blue-300 shrink-0 mt-0.5" weight="fill" />
                    {item}
                  </li>
                ))}
              </ul>
              {/* HouseProud is a planned sub-brand; the domain isn't ours
                  yet, so we keep the CTA visually present (button styling)
                  but render as a span -- no navigation. Swap back to an
                  anchor when the domain is registered. The "Coming soon"
                  pill makes the unavailability explicit so we don't tease
                  a destination that doesn't exist. */}
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <div className="relative inline-block">
                  <Button
                    asChild
                    size="lg"
                    className="h-11 px-7 text-sm bg-white/40 text-blue-100 cursor-not-allowed font-bold opacity-80"
                    disabled
                  >
                    <span>
                      See HouseProud
                      <ArrowRight className="ml-2 h-4 w-4" weight="bold" />
                    </span>
                  </Button>
                  <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shadow-md">
                    Coming soon
                  </span>
                </div>
                <p className="text-xs text-blue-200">
                  Free for homeowners when it launches.
                </p>
              </div>
            </div>

            {/* Right -- property logbook visual */}
            <div className="hidden lg:block">
              <div className="rounded-lg bg-card shadow-lg shadow-blue-950/20 p-6 space-y-4 max-w-sm ml-auto">
                {/* Card header */}
                <div className="flex items-center gap-3 pb-4 border-b border-border">
                  <div className="h-10 w-10 rounded-md bg-blue-50 flex items-center justify-center text-xl">
                    {"🏠"}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">42 Maple Road</p>
                    <p className="text-xs text-muted-foreground">Property logbook &middot; 3 records</p>
                  </div>
                  <div className="ml-auto">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                      Safe
                      <CheckCircle className="h-3 w-3" weight="fill" />
                    </span>
                  </div>
                </div>
                {/* Log entries -- country-keyed, see buildEntries above. */}
                {entries.map((entry) => (
                  <div key={entry.label} className="flex items-start gap-3">
                    <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${entry.dot}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{entry.label}</p>
                      <p className="text-xs text-muted-foreground">{entry.sub}</p>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold mt-1 inline-block ${entry.badgeColor}`}>
                        {entry.badge}
                      </span>
                    </div>
                  </div>
                ))}
                {/* Footer */}
                <div className="pt-3 border-t border-border flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">Powered by HouseProud</p>
                  <p className="text-xs font-semibold text-blue-700">Share history &rarr;</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </FadeInOnScroll>
  );
}
